import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../../lib/api';
import { ApiError, type RehostItemResult, type RehostResponse } from '../../types/api';

/** More rounds than this means something is wrong (the server copies 100 images at most, several per round). */
const MAX_ROUNDS = 60;

export type RehostErrorKind = 'not_configured' | 'forbidden' | 'too_many' | 'stalled' | 'failed';

export interface RehostRun {
  status: 'idle' | 'running' | 'done' | 'error';
  /** Every image handled so far, in the order the server answered. */
  results: RehostItemResult[];
  /** Images the server had not tried yet after the last round; null before the first answer. */
  remaining: number | null;
  error: { kind: RehostErrorKind; message: string } | null;
}

const IDLE: RehostRun = { status: 'idle', results: [], remaining: null, error: null };

function errorOf(err: unknown): { kind: RehostErrorKind; message: string } {
  if (err instanceof ApiError) {
    if (err.status === 503 && err.body.error === 'media_not_configured') {
      return {
        kind: 'not_configured',
        message:
          'Image storage is not set up yet, so images cannot be copied. Ask the administrator to set it up. Nothing was changed.',
      };
    }
    if (err.status === 403) {
      return {
        kind: 'forbidden',
        message: 'Your role cannot copy images. Ask an editor or an admin.',
      };
    }
    if (err.status === 422 && err.body.error === 'too_many_images') {
      return {
        kind: 'too_many',
        message: err.body.message ?? 'There are too many different images to copy in one run.',
      };
    }
    if (err.status === 413) {
      return {
        kind: 'failed',
        message: 'The HTML is too large to send for copying (limit 10 MB).',
      };
    }
  }
  return { kind: 'failed', message: 'The images could not be copied. Try again shortly.' };
}

/**
 * "Copy all to NP-Flow": asks the server to copy the images of `html` to
 * our own media domain, round after round (`POST /media/rehost`). One round
 * copies as many images as one Worker request can afford and says how many
 * are `remaining`; this repeats with the HTML it got back until none is,
 * sending the images that failed as `skip` so none is tried twice. A failed
 * image is a result, not an error: the rest go through.
 *
 * `onHtml` gets the new HTML once, when the run ends (also when it ends in
 * an error, so the images already copied are kept). The caller should not
 * let the HTML be edited while `status` is `running`.
 */
export function useRehostImages(html: string, onHtml: (html: string) => void) {
  const [run, setRun] = useState<RehostRun>(IDLE);
  const alive = useRef(true);
  const busy = useRef(false);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const start = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    let current = html;
    const skip: string[] = [];
    const results: RehostItemResult[] = [];
    let error: RehostRun['error'] = null;
    let remaining: number | null = null;
    setRun({ status: 'running', results: [], remaining: null, error: null });
    try {
      for (let round = 0; round < MAX_ROUNDS; round += 1) {
        const res = await apiFetch<RehostResponse>('/media/rehost', {
          method: 'POST',
          body: { html: current, skip },
        });
        current = res.html;
        remaining = res.remaining;
        results.push(...res.results);
        for (const r of res.results) if (r.status === 'failed') skip.push(r.key);
        if (alive.current) setRun({ status: 'running', results: [...results], remaining, error });
        if (res.remaining === 0) break;
        if (res.results.length === 0) {
          error = {
            kind: 'stalled',
            message: 'The server did not copy anything in the last round. Try again.',
          };
          break;
        }
      }
      if (remaining !== 0 && error === null) {
        error = { kind: 'stalled', message: 'The copy took too many rounds. Try again.' };
      }
    } catch (err) {
      error = errorOf(err);
    } finally {
      busy.current = false;
    }
    if (!alive.current) return;
    if (current !== html) onHtml(current);
    setRun({ status: error ? 'error' : 'done', results, remaining, error });
  }, [html, onHtml]);

  const reset = useCallback(() => setRun(IDLE), []);
  return { run, start, reset };
}
